import type { ReactNode } from 'react';
import type { FragmentType } from 'src/gql';
import { useState } from 'react';
import { Separator } from '@savvy-kit/components/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { Info } from 'lucide-react';
import ColorCodedPerformanceTypography from 'src/components/performance-report/views/color-coded-return';
import { RETURNS_CALCULATION_INFO_TOOLTIP } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';

type TooltipInfoIconPlacement =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-start'
  | 'top-end'
  | 'right-start'
  | 'right-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end';

const PLACEMENT_MAP: Record<
  TooltipInfoIconPlacement,
  { side: 'top' | 'right' | 'bottom' | 'left'; align: 'start' | 'center' | 'end' }
> = {
  top: { side: 'top', align: 'center' },
  right: { side: 'right', align: 'center' },
  bottom: { side: 'bottom', align: 'center' },
  left: { side: 'left', align: 'center' },
  'top-start': { side: 'top', align: 'start' },
  'top-end': { side: 'top', align: 'end' },
  'right-start': { side: 'right', align: 'start' },
  'right-end': { side: 'right', align: 'end' },
  'bottom-start': { side: 'bottom', align: 'start' },
  'bottom-end': { side: 'bottom', align: 'end' },
  'left-start': { side: 'left', align: 'start' },
  'left-end': { side: 'left', align: 'end' },
};

function splitPlacement(placement: TooltipInfoIconPlacement): {
  side: 'top' | 'right' | 'bottom' | 'left';
  align: 'start' | 'center' | 'end';
} {
  return PLACEMENT_MAP[placement];
}

export const PerformanceReturnsDetailsFragment = graphql(/* GraphQL */ `
  fragment PerformanceReturnsDetails on Performance {
    twr
  }
`);

export function TooltipWrapper({
  title,
  placement,
  children,
  justify,
}: {
  readonly title: string | ReactNode;
  readonly placement: TooltipInfoIconPlacement;
  readonly children: ReactNode;
  readonly justify?: 'flex-start' | 'flex-end';
}) {
  return (
    <div className={cn('flex flex-row items-center gap-1', justify === 'flex-end' ? 'justify-end' : 'justify-start')}>
      {children}
      <TooltipInfoIcon title={title} placement={placement} />
    </div>
  );
}

export function TooltipInfoIcon({
  title,
  placement,
  className,
}: {
  readonly title: string | ReactNode;
  readonly placement: TooltipInfoIconPlacement;
  readonly className?: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const { side, align } = splitPlacement(placement);
  return (
    <Tooltip
      open={showTooltip}
      onOpenChange={(open) => {
        setShowTooltip(open);
      }}
    >
      <TooltipTrigger
        render={
          <Info
            className={cn('text-secondary size-4', className)}
            onClick={() => {
              setShowTooltip(!showTooltip);
            }}
          />
        }
      />
      <TooltipContent side={side} align={align}>
        {title}
      </TooltipContent>
    </Tooltip>
  );
}

function PerformanceReturnsDetails({
  performanceFrag,
}: {
  readonly performanceFrag: FragmentType<typeof PerformanceReturnsDetailsFragment> | undefined;
}) {
  const performance = unmaskFragment(PerformanceReturnsDetailsFragment, performanceFrag);
  if (isNil(performance)) {
    return null;
  }

  return (
    <>
      <Separator orientation="vertical" className="bg-border-light" />
      <div className="flex flex-col gap-3 items-start justify-start">
        <TooltipWrapper title={RETURNS_CALCULATION_INFO_TOOLTIP} placement="right">
          <Text color="secondary" variant="overline">
            Return
          </Text>
        </TooltipWrapper>
        <ColorCodedPerformanceTypography
          performance={isNil(performance.twr) ? null : parseFloat(performance.twr)}
          variant="body"
        />
      </div>
    </>
  );
}

export default PerformanceReturnsDetails;
